<template>
  <v-dialog v-if="!embedded" v-model="dialog" max-width="1400px" persistent>
    <div class="wage-nontax-compare-modal">
      <nontax-compare-panel
        :active-tab="activeTab"
        :current-year-month="currentYearMonth"
        :comparison-data="comparisonData"
        :totals="totals"
        :custom-lunch="customLunch"
        :no-production-nontax="noProductionNontax"
        :before-description="beforeDescription"
        :is-generating-pdf="isGeneratingPdf"
        :show-close="true"
        :hide-scenario-settings="hideScenarioSettings"
        @update:active-tab="activeTab = $event"
        @update:custom-lunch="customLunch = $event"
        @update:no-production-nontax="noProductionNontax = $event"
        @setting-change="onSettingChange"
        @generate-pdf="generatePdf"
        @close="closeDialog"
      />
    </div>
  </v-dialog>
  <div v-else class="wage-nontax-compare-modal wage-nontax-compare-modal--embedded">
    <nontax-compare-panel
      :active-tab="activeTab"
      :current-year-month="currentYearMonth"
      :comparison-data="comparisonData"
      :totals="totals"
      :custom-lunch="customLunch"
      :no-production-nontax="noProductionNontax"
      :before-description="beforeDescription"
      :is-generating-pdf="isGeneratingPdf"
      :show-close="false"
      :hide-scenario-settings="hideScenarioSettings"
      @update:active-tab="activeTab = $event"
      @update:custom-lunch="customLunch = $event"
      @update:no-production-nontax="noProductionNontax = $event"
      @setting-change="onSettingChange"
      @generate-pdf="generatePdf"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import axios from 'axios';
import { tokenManager } from '@/services/api';
import type { CommonWageListRow } from '@/types/commonWage';
import NontaxComparePanel from '@/components/wage/common/NontaxComparePanel.vue';
import type { NontaxDiffTooltipSet } from '@/utils/nontaxDiffTooltip';
import { buildRowDiffTooltips, buildTotalsDiffTooltips, aggregateSavingContext, type NontaxTotalsDiffSums } from '@/utils/nontaxDiffTooltip';
import {
  calcBeforeEmployeeDeductionFromFacility,
  calcAfterEmployeeDeductionFromFacility,
} from '@/utils/nontaxBeforeDeduction';
import { calcBeforeCompanyInsuranceFromFacility } from '@/utils/nontaxBeforeCompanyIns';
import { calcOrdinaryWageRiskFromFacilityWage, calcAnnualOrdinaryWageRisk } from '@/utils/ordinaryWageRisk';
import {
  calcNontaxImprovement,
  calcMarginalSavingOnImprovement,
  inferFacilityInsuranceFlags,
} from '@/utils/nontaxImprovementSaving';

export interface NontaxComparisonItem {
  empName: string;
  empJob: string;
  total: string;
  before: {
    nonTax: string;
    companyIns: string;
    retirement: string;
    netPay: string;
  };
  after: {
    nonTax: string;
    companyIns: string;
    retirement: string;
    netPay: string;
  };
  diff: {
    nonTax: string;
    companyIns: string;
    retirement: string;
    netPay: string;
  };
  saving: string;
  ordinaryWageRiskMonthly: string;
  diffTooltip?: NontaxDiffTooltipSet;
}

export interface NontaxComparisonTotals {
  total: string;
  before: {
    nonTax: string;
    companyIns: string;
    retirement: string;
    netPay: string;
  };
  after: {
    nonTax: string;
    companyIns: string;
    retirement: string;
    netPay: string;
  };
  diff: {
    nonTax: string;
    companyIns: string;
    retirement: string;
    netPay: string;
  };
  saving: string;
  yearSaving: string;
  ordinaryWageRisk: string;
  ordinaryWageRisk1Year: string;
  ordinaryWageRiskMonthly: string;
  diffTooltip?: NontaxDiffTooltipSet;
}

interface TaxRate {
  tr_yeon: number;
  tr_gun: number;
  tr_jang: number;
  tr_go: number;
  tr_sil: number;
  tr_san: number;
}

interface IncomeTaxRow {
  income1: number;
  income2: number;
  depe_1: number;
}

const props = withDefaults(
  defineProps<{
    wageList?: CommonWageListRow[];
    taxRate: TaxRate;
    incomeTaxTable?: IncomeTaxRow[];
    year: number;
    month: number;
    memberName?: string;
    embedded?: boolean;
    externalMode?: boolean;
    hideScenarioSettings?: boolean;
  }>(),
  {
    wageList: () => [],
    incomeTaxTable: () => [],
    embedded: false,
    externalMode: false,
    hideScenarioSettings: false,
  },
);

const emit = defineEmits<{
  'settings-change': [payload: { customLunch: string; noProductionNontax: boolean }];
}>();

const dialog = ref(false);
const activeTab = ref('detail');
const comparisonData = ref<NontaxComparisonItem[]>([]);
const currentYearMonth = ref('');
const customLunch = ref('');
const noProductionNontax = ref(false);
const isGeneratingPdf = ref(false);

const beforeDescription = computed(() => {
  if (props.hideScenarioSettings) {
    return '업로드 중식비 + 차량보조비/양육비/당직수당 (생산직 비과세 제외)';
  }
  const lunchVal = parseFloat(customLunch.value.toString().replace(/,/g, '')) || 0;
  const parts = [];
  if (customLunch.value !== '') {
    parts.push(`중식비 ${lunchVal.toLocaleString()}원 적용`);
  } else {
    parts.push('중식비');
  }
  parts.push('차량보조비/양육비/당직수당');
  if (noProductionNontax.value) {
    return `${parts.join(' + ')} (생산직 비과세 제외)`;
  }
  return `${parts.join('/') } 비과세`;
});

const totals = reactive<NontaxComparisonTotals>({
  total: '0',
  before: { nonTax: '0', companyIns: '0', retirement: '0', netPay: '0' },
  after: { nonTax: '0', companyIns: '0', retirement: '0', netPay: '0' },
  diff: { nonTax: '0', companyIns: '0', retirement: '0', netPay: '0' },
  saving: '0',
  yearSaving: '0',
  ordinaryWageRisk: '0',
  ordinaryWageRisk1Year: '0',
  ordinaryWageRiskMonthly: '0',
  diffTooltip: undefined,
});

const applyTotals = (source: NontaxComparisonTotals) => {
  totals.total = source.total;
  totals.before = { ...source.before };
  totals.after = { ...source.after };
  totals.diff = { ...source.diff };
  totals.saving = source.saving;
  totals.yearSaving = source.yearSaving;
  totals.ordinaryWageRisk = source.ordinaryWageRisk;
  totals.ordinaryWageRisk1Year = source.ordinaryWageRisk1Year;
  totals.ordinaryWageRiskMonthly = source.ordinaryWageRiskMonthly;
  totals.diffTooltip = source.diffTooltip;
};

const openDialog = () => {
  dialog.value = true;
  activeTab.value = 'detail';
  currentYearMonth.value = `${props.year}년 ${props.month}월`;
  customLunch.value = '';
  noProductionNontax.value = false;
  if (!props.externalMode) {
    calculateComparison();
  }
};

const applyComparison = (
  data: NontaxComparisonItem[],
  totalsSource: NontaxComparisonTotals,
  year?: number,
  month?: number,
) => {
  comparisonData.value = data;
  applyTotals(totalsSource);
  if (year && month) {
    currentYearMonth.value = `${year}년 ${month}월`;
  }
};

const resetComparison = () => {
  comparisonData.value = [];
  applyTotals({
    total: '0',
    before: { nonTax: '0', companyIns: '0', retirement: '0', netPay: '0' },
    after: { nonTax: '0', companyIns: '0', retirement: '0', netPay: '0' },
    diff: { nonTax: '0', companyIns: '0', retirement: '0', netPay: '0' },
    saving: '0',
    yearSaving: '0',
    ordinaryWageRisk: '0',
    ordinaryWageRisk1Year: '0',
    ordinaryWageRiskMonthly: '0',
  });
};

defineExpose({ openDialog, applyComparison, resetComparison });

const closeDialog = () => {
  dialog.value = false;
};

const onSettingChange = () => {
  if (props.externalMode) {
    emit('settings-change', {
      customLunch: customLunch.value,
      noProductionNontax: noProductionNontax.value,
    });
    return;
  }
  calculateComparison();
};

const nc = (value: string | number | null | undefined): number => {
  if (value == null) return 0;
  return parseFloat(value.toString().replace(/,/g, '')) || 0;
};

const ac = (value: number): string => {
  if (isNaN(value)) return '0';
  return Math.round(value).toLocaleString();
};

const calculateComparison = () => {
  if (!props.wageList || props.wageList.length === 0) return;

  const tax = props.taxRate;
  const sum = { total: 0 };
  const sumBefore = { nonTax: 0, companyIns: 0, retirement: 0, netPay: 0 };
  const sumAfter = { nonTax: 0, companyIns: 0, retirement: 0, netPay: 0 };
  let sumOrdinaryWageRisk = 0;
  let sumOrdinaryWageRisk1Year = 0;
  let sumOrdinaryWageRiskMonthly = 0;
  const savingContexts: Array<{
    improvement: ReturnType<typeof calcNontaxImprovement>;
    savingBreakdown: ReturnType<typeof calcMarginalSavingOnImprovement>;
  }> = [];

  const inputLunch = nc(customLunch.value);
  const useCustomLunch = customLunch.value !== '' && inputLunch >= 0;
  const excludeProductionNontax = noProductionNontax.value;

  comparisonData.value = props.wageList.map((wage) => {
    const total = nc(wage.cwhd_wage_total);
    const afterTaxable = nc(wage.cwhd_taxable);
    const afterNonTax = total - afterTaxable;
    const afterDeduction = nc(wage.cwhd_l_total);
    const afterNetPay = total - afterDeduction;
    const afterRetirement = nc(wage.cwhd_c_out);

    const after_c_yeon = nc(wage.cwhd_c_yeon);
    const after_c_gun = nc(wage.cwhd_c_gun);
    const after_c_jang = nc(wage.cwhd_c_jang);
    const after_c_go = nc(wage.cwhd_c_go);
    const after_c_sil = nc(wage.cwhd_c_sil);
    const after_c_san = nc(wage.cwhd_c_san);

    const originalLunch = nc(wage.cwhd_lunch);
    const lunch = useCustomLunch ? Math.min(originalLunch, inputLunch) : originalLunch;
    const car = nc(wage.cwhd_car);
    const childcare = nc(wage.cwhd_child);
    const dang = nc(wage.cwhd_dang);

    const beforeNonTax = Math.floor((lunch + car + childcare + dang) / 10) * 10;
    const beforeTaxable = Math.floor((total - beforeNonTax) / 10) * 10;

    const beforeDeductionParts = calcBeforeEmployeeDeductionFromFacility(
      beforeTaxable,
      tax,
      props.incomeTaxTable,
      wage,
    );
    const beforeDeduction = beforeDeductionParts.l_total;
    const beforeNetPay = total - beforeDeduction;
    const afterDeductionParts = calcAfterEmployeeDeductionFromFacility(wage);

    const companyIns = calcBeforeCompanyInsuranceFromFacility(
      beforeTaxable,
      afterTaxable,
      tax,
      wage,
    );
    const beforeCompanyIns = companyIns.beforeTotal;
    const afterCompanyIns = companyIns.afterTotal;
    const beforeRetirement = afterRetirement === 0 ? 0 : Math.floor(((total - lunch) / 12) / 10) * 10;

    const diffNonTax = beforeNonTax - afterNonTax;
    const diffCompanyIns = beforeCompanyIns - afterCompanyIns;
    const diffRetirement = beforeRetirement - afterRetirement;
    const diffNetPay = beforeNetPay - afterNetPay;

    const processedLunch = nc(wage.cwhd_lunch);
    const improvement = calcNontaxImprovement({
      uploadLunch: lunch,
      improvedLunch: processedLunch,
      onhOvertime: nc(wage.cwhd_overtime),
      onhNight: nc(wage.cwhd_night),
      onhHoliday: nc(wage.cwhd_holiday),
      applyOnhImprovement: !excludeProductionNontax,
    });
    const savingBreakdown = calcMarginalSavingOnImprovement({
      improvement,
      afterTaxable,
      tax,
      getSo: props.incomeTaxTable,
      flags: inferFacilityInsuranceFlags(wage),
      afterCompanyIns: companyIns.after,
      afterRetirement,
    });
    const saving = savingBreakdown.total;
    savingContexts.push({ improvement, savingBreakdown });

    const usualRiskBreakdown = calcOrdinaryWageRiskFromFacilityWage(wage);
    const ordinaryWageRiskMonthly = usualRiskBreakdown.monthlyRisk;
    const ordinaryWageRisk = usualRiskBreakdown.risk3Year;
    const ordinaryWageRisk1Year = calcAnnualOrdinaryWageRisk(usualRiskBreakdown);
    sumOrdinaryWageRisk += ordinaryWageRisk;
    sumOrdinaryWageRisk1Year += ordinaryWageRisk1Year;
    sumOrdinaryWageRiskMonthly += ordinaryWageRiskMonthly;

    const beforeParts = [
      { label: '중식비', amount: lunch },
      { label: '차량보조비', amount: car },
      { label: '양육비', amount: childcare },
      { label: '당직수당', amount: dang },
    ];

    const afterParts = [
      { label: '중식비', amount: processedLunch },
      { label: '차량보조비', amount: car },
      { label: '양육비', amount: childcare },
      { label: '당직수당', amount: dang },
      { label: '생산직비과세(연·야·휴)', amount: nc(wage.cwhd_overtime_nontax) + nc(wage.cwhd_night_nontax) + nc(wage.cwhd_holiday_nontax) },
    ];

    const diffTooltip = buildRowDiffTooltips({
      nonTax: {
        beforeNonTax,
        afterNonTax,
        diffNonTax,
        total,
        afterTaxable,
        beforeParts,
        afterParts,
      },
      companyIns: {
        diffCompanyIns,
        beforeTaxable,
        afterTaxable,
        companyIns,
      },
      retirement: {
        diffRetirement,
        beforeRetirement,
        afterRetirement,
        total,
        uploadLunch: lunch,
      },
      netPay: {
        diffNetPay,
        beforeNetPay,
        afterNetPay,
        total,
        beforeDeduction,
        afterDeduction,
        beforeDeductionParts,
        afterDeductionParts,
        beforeTaxable,
        afterTaxable,
      },
      saving: {
        saving,
        improvement,
        savingBreakdown,
      },
      ordinaryWageRisk: usualRiskBreakdown,
    });

    sum.total += total;
    sumBefore.nonTax += beforeNonTax;
    sumBefore.companyIns += beforeCompanyIns;
    sumBefore.retirement += beforeRetirement;
    sumBefore.netPay += beforeNetPay;
    sumAfter.nonTax += afterNonTax;
    sumAfter.companyIns += afterCompanyIns;
    sumAfter.retirement += afterRetirement;
    sumAfter.netPay += afterNetPay;

    return {
      empName: wage.ce_name,
      empJob: wage.ce_position || '',
      total: ac(total),
      before: {
        nonTax: ac(beforeNonTax),
        companyIns: ac(beforeCompanyIns),
        retirement: ac(beforeRetirement),
        netPay: ac(beforeNetPay),
      },
      after: {
        nonTax: ac(afterNonTax),
        companyIns: ac(afterCompanyIns),
        retirement: ac(afterRetirement),
        netPay: ac(afterNetPay),
      },
      diff: {
        nonTax: ac(diffNonTax),
        companyIns: ac(diffCompanyIns),
        retirement: ac(diffRetirement),
        netPay: ac(diffNetPay),
      },
      saving: ac(saving),
      ordinaryWageRiskMonthly: ac(ordinaryWageRiskMonthly),
      diffTooltip,
    };
  });

  const diffNonTax = sumBefore.nonTax - sumAfter.nonTax;
  const diffCompanyIns = sumBefore.companyIns - sumAfter.companyIns;
  const diffRetirement = sumBefore.retirement - sumAfter.retirement;
  const diffNetPay = sumBefore.netPay - sumAfter.netPay;
  const savingContext = aggregateSavingContext(savingContexts);
  const saving = savingContext.saving;

  const totalsSums: NontaxTotalsDiffSums = {
    beforeNonTax: sumBefore.nonTax,
    afterNonTax: sumAfter.nonTax,
    beforeCompanyIns: sumBefore.companyIns,
    afterCompanyIns: sumAfter.companyIns,
    beforeRetirement: sumBefore.retirement,
    afterRetirement: sumAfter.retirement,
    beforeNetPay: sumBefore.netPay,
    afterNetPay: sumAfter.netPay,
    ordinaryWageRisk: sumOrdinaryWageRisk,
    ordinaryWageRisk1Year: sumOrdinaryWageRisk1Year,
    ordinaryWageRiskMonthly: sumOrdinaryWageRiskMonthly,
  };

  applyTotals({
    total: ac(sum.total),
    before: {
      nonTax: ac(sumBefore.nonTax),
      companyIns: ac(sumBefore.companyIns),
      retirement: ac(sumBefore.retirement),
      netPay: ac(sumBefore.netPay),
    },
    after: {
      nonTax: ac(sumAfter.nonTax),
      companyIns: ac(sumAfter.companyIns),
      retirement: ac(sumAfter.retirement),
      netPay: ac(sumAfter.netPay),
    },
    diff: {
      nonTax: ac(diffNonTax),
      companyIns: ac(diffCompanyIns),
      retirement: ac(diffRetirement),
      netPay: ac(diffNetPay),
    },
    saving: ac(saving),
    yearSaving: ac(saving * 12),
    ordinaryWageRisk: ac(sumOrdinaryWageRisk),
    ordinaryWageRisk1Year: ac(sumOrdinaryWageRisk1Year),
    ordinaryWageRiskMonthly: ac(sumOrdinaryWageRiskMonthly),
    diffTooltip: buildTotalsDiffTooltips(
      totalsSums,
      {
        nonTax: diffNonTax,
        companyIns: diffCompanyIns,
        retirement: diffRetirement,
        netPay: diffNetPay,
        saving,
      },
      savingContext,
    ),
  });
};

const generatePdf = async () => {
  if (comparisonData.value.length === 0) {
    alert('비교 데이터가 없습니다.');
    return;
  }

  isGeneratingPdf.value = true;
  try {
    const token = tokenManager.getToken();
    const response = await axios.post('/api/common-wage/nontax-compare-pdf', {
      comparisonData: comparisonData.value,
      totals: {
        total: totals.total,
        before: totals.before,
        after: totals.after,
        diff: totals.diff,
        saving: totals.saving,
        yearSaving: totals.yearSaving,
        ordinaryWageRisk: totals.ordinaryWageRisk,
        ordinaryWageRisk1Year: totals.ordinaryWageRisk1Year,
      },
      memberName: props.memberName || '기관명',
      yearMonth: { year: props.year, month: props.month },
    }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (response.data.success) {
      const pdfBase64 = response.data.pdf;
      const binaryString = window.atob(pdfBase64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        alert('팝업 차단 설정을 확인해주세요.');
      }
    } else {
      alert(`PDF 생성 실패: ${response.data.message || '알 수 없는 오류'}`);
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`PDF 생성 중 오류: ${message}`);
    console.error('PDF 생성 오류:', error);
  } finally {
    isGeneratingPdf.value = false;
  }
};
</script>
